import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { SharedLanguageService } from '@language';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { SingleValueFormInput } from '../../../../helpers/single-value-form-input';
import { FormSelectOption } from '../../../entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../entities/multi-form-select-options.entity';
import { ShAutocompleteComponent } from '../../_core/autocomplete/autocomplete.component';
import { ShAutoFocusDirective } from '../../../../elements/directives/auto-focus/auto-focus.directive';

@Component({
    selector: 'sh-form-text-complex-base',
    templateUrl: './form-text-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormTextComplexBaseComponent)],
    standalone: true,
    imports: [ShAutocompleteComponent, ReactiveFormsModule, AsyncPipe, ShAutoFocusDirective]
})
export class ShFormTextComplexBaseComponent extends SingleValueFormInput<string | null> implements OnInit, OnDestroy {
    // IMPORTANT: This component produces side effect on its parent. When changing anything, make sure to test properly.
    public _maxLength: number = 32766; // Max allowed by Engine for simple fields

    @Input() autocomplete = 'off';
    @Input() placeholder = '';
    @Input() id!: string;

    @Input() invalid?: boolean = false;
    @Input() describeLabelId = '';
    @Input() hasActionItems = false;

    @Input() indeterminateMsg = '';

    @Input() inOverflownContainer = false;

    @Input() autocompleteFct?: (term: string) => Observable<MultiFormSelectOptions>;
    @Input() autocompleteDebounce: number = 300;
    @Input() autocompleteLoadingMessage: string;
    @Input() autocompleteNoResultsMessage: string;
    @Input() autocompleteLockOnInvalid: boolean = false;
    @Input() set maxLength(length: number) {
        if (length) {
            this._maxLength = Math.min(length, this._maxLength);
        }
    }

    @Input() autoFocus: boolean = false;

    @Output() selectedSuggestions: EventEmitter<FormSelectOption<string>[]> = new EventEmitter<
        FormSelectOption<string>[]
    >();

    @Output() internalError: EventEmitter<string> = new EventEmitter<string>();

    public control: UntypedFormControl;

    public readonly indeterminate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public message$!: Observable<string>;

    // Injectors
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);

    constructor(changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);
        this.control = new UntypedFormControl();

        this.initPlaceholderMessage();
    }

    ngOnInit(): void {
        this.initListeners();

        this.control.valueChanges.subscribe(() => {
            if (this.control.getError('maxlength')) {
                // Validator set by maxlength property
                this.internalError.emit(this.translateService.instant('errors.max-length', { limit: this._maxLength }));
            } else {
                this.internalError.emit('');
            }
        });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.indeterminate$.complete();
    }

    initPlaceholderMessage(): void {
        this.message$ = this.indeterminate$.pipe(
            map(bool => {
                if (bool) {
                    return this.indeterminateMsg;
                } else {
                    return this.placeholder;
                }
            })
        );
    }

    initListeners(): void {
        const input$: Observable<string> = this.control.valueChanges;

        const firstChar$: Observable<string> = input$.pipe(
            filter(value => !!value),
            take(1)
        );

        firstChar$.pipe(takeUntil(this.destroy$)).subscribe(() => this.indeterminate$.next(false));

        input$.pipe(takeUntil(this.destroy$)).subscribe(value => {
            if (value) {
                this.propagateChange(new FormValue(value.trim(), FormValueState.SET));
            } else {
                this.propagateChange(new FormValue(null, FormValueState.UNSET));
            }
        });
    }

    public unset(): void {
        this.indeterminate$.next(false);
        this.control.setValue(null);
        this.propagateChange(new FormValue(null, FormValueState.UNSET));
    }

    public set(value: string | null): void {
        if (value === null || value === undefined) {
            return this.unset();
        }

        if (typeof value !== 'string') {
            console.error(`ShFormTextComplexBaseComponent:: FormValue does not contain a string: ${value}`);
            this.unset();
            return;
        }

        this.indeterminate$.next(false);
        this.control.setValue(value);
    }

    public makeIndeterminate(): void {
        this.indeterminate$.next(true);
    }

    public suggestionsSelected(suggestions: FormSelectOption<string>[]): void {
        this.selectedSuggestions.emit(suggestions);
    }
}
